import { logError, logInfo } from "./utils/log";
import { getTotalPagesResolved } from "./utils/getTotalPagesResolved";
import { isDomainResolved } from "./utils/isDomainResolved";
import { isDarkMode } from "./utils/isDarkMode";

import "./popup.scss";

// Shared function to update popup logo based on enabled and resolved status
const updateLogoElement = (isEnabled: boolean, isResolved: boolean = false) => {
  const logo = document.getElementById("logo") as HTMLImageElement;
  if (!logo) return;
  if (!isEnabled) {
    logo.src = "icons/logo-off.png";
  } else if (isResolved) {
    logo.src = "icons/logo-on-green.png";
  } else {
    logo.src = "icons/logo-on.png";
  }
};

// Localize UI elements
const localizeUI = () => {
  document.getElementById("extension-title")!.textContent =
    chrome.i18n.getMessage("extName");
  document.getElementById("status-text")!.textContent = chrome.i18n.getMessage(
    "cookieBannerResolved"
  );
};

// Update total counter from storage
const updateTotalCounter = async () => {
  const total = await getTotalPagesResolved();

  // Update status text with count
  const toggleTotalElement = document.getElementById("toggle-text-total");
  if (!toggleTotalElement) return;
  toggleTotalElement.textContent = chrome.i18n.getMessage("toggleTotal", [
    String(total),
  ]);
};

// Initialize and handle the toggle checkbox
const initToggle = () => {
  const toggleCheckbox = document.getElementById(
    "cookie-blocker-toggle"
  ) as HTMLInputElement;
  const toggleTitle = document.getElementById("toggle-title") as HTMLElement;

  const updateToggleTitle = (isEnabled: boolean) => {
    if (toggleTitle)
      toggleTitle.textContent = chrome.i18n.getMessage(
        isEnabled ? "toggleTitleOn" : "toggleTitleOff"
      );
  };

  chrome.storage.local.get(["cookieBlockerEnabled"], (result) => {
    const isEnabled = result.cookieBlockerEnabled !== false;
    if (toggleCheckbox) toggleCheckbox.checked = isEnabled;
    updateToggleTitle(isEnabled);
    updateLogoElement(isEnabled);
    if (result.cookieBlockerEnabled === undefined) {
      chrome.storage.local.set({ cookieBlockerEnabled: true });
    }
  });

  toggleCheckbox?.addEventListener("change", async () => {
    const isEnabled = toggleCheckbox.checked;
    updateToggleTitle(isEnabled);
    updateLogoElement(isEnabled);

    // persist new setting
    await chrome.storage.local.set({ cookieBlockerEnabled: isEnabled });
    logInfo(`Cookie blocker ${isEnabled ? "enabled" : "disabled"}`);

    // get current tab and update its action icon
    const [activeTab] = await chrome.tabs.query({
      active: true,
      currentWindow: true,
    });
    if (activeTab?.id !== undefined) {
      const isDark = await isDarkMode();
      const darkModeSuffix = isDark ? "-darkmode" : "";
      await chrome.action.setIcon({
        tabId: activeTab.id,
        path: {
          "16": isEnabled
            ? `icons/icon-blue-check${darkModeSuffix}.png`
            : "icons/icon-gray-check.png",
          "32": isEnabled
            ? `icons/icon-blue-check32${darkModeSuffix}.png`
            : "icons/icon-gray-check32.png",
        },
      });
    }

    // refresh banner‐resolved status and popup logo
    await updateStatusVisibility();
  });
};

// Set or unset dark mode on body
const initDarkMode = async () => {
  const isDark = await isDarkMode();
  if (isDark) {
    document.body.classList.add("darkmode");
  } else {
    document.body.classList.remove("darkmode");
  }
};

const updateStatusVisibility = async () => {
  try {
    // Get the current tab's URL
    const tabs = await chrome.tabs.query({ active: true, currentWindow: true });
    if (tabs[0]?.url) {
      const currentUrl = new URL(tabs[0].url);
      const currentDomain = currentUrl.hostname.includes("www.")
        ? currentUrl.hostname.replace("www.", "")
        : currentUrl.hostname;

      // Update the status element visibility
      const statusElement = document.querySelector(".status") as HTMLElement;
      const statusElementText = document.querySelector(
        "#status-text"
      ) as HTMLElement;
      if (statusElement) {
        const isResolved = await isDomainResolved(currentDomain);
        if (isResolved) {
          statusElement.style.display = "flex";
          statusElementText.textContent = chrome.i18n.getMessage(
            "cookieBannerResolved"
          );
          const result = await chrome.storage.local.get([
            "cookieBlockerEnabled",
          ]);
          const isEnabled = result.cookieBlockerEnabled !== false;
          updateLogoElement(isEnabled, true);
        } else {
          statusElement.style.display = "none";
        }
      }
    }
  } catch (error) {
    logError("Error checking domain resolution status:", error);
  }
};

// Load data when DOM is ready
document.addEventListener("DOMContentLoaded", async () => {
  localizeUI();
  await initDarkMode();
  initToggle();
  await updateTotalCounter();
  await updateStatusVisibility();
});
